import type { Components, Theme } from '@mui/material/styles';
import type { CSSObject } from '@mui/material/styles';
import { createFocusRingStyles, createMotionTransition } from './interaction';
import { designTokens } from './tokens';

function ChoiceMark({ kind, state }: { kind: 'checkbox' | 'radio'; state?: 'checked' | 'mixed' }) {
  return (
    <span className="HeydayChoice-mark" data-kind={kind} aria-hidden="true">
      {kind === 'checkbox' && state && (
        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {state === 'mixed' ? <path d="M3 8h10" /> : <path d="m4 8 3 3 5-6" />}
        </svg>
      )}
    </span>
  );
}

/** 표시 도형과 실제 입력 영역을 분리합니다. 표의 native mixed/키보드 계약도 MUI가 유지합니다. */
export function createChoiceComponents(contrastText: string): Components<Theme> {
  const selected = '&.Mui-checked, &.MuiCheckbox-indeterminate';
  const choiceRoot: CSSObject = {
    width: 24,
    height: 24,
    padding: 4,
    flexShrink: 0,
    borderRadius: designTokens.shape.sm,
    color: designTokens.color.secondaryText,
    '&:hover': { backgroundColor: 'transparent' },
    '& .HeydayChoice-mark': {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      flexShrink: 0,
      boxSizing: 'border-box',
      border: `1px solid ${designTokens.color.secondaryText}`,
      borderRadius: designTokens.shape.xs,
      backgroundColor: designTokens.color.canvas,
      transition: createMotionTransition(['background-color', 'border-color'], 'feedback'),
      '&[data-kind="radio"]': { borderRadius: designTokens.shape.full },
      '& svg': { position: 'absolute', flexShrink: 0 },
    },
    '@media (forced-colors: none)': { '&:not(:has(input:disabled)):hover .HeydayChoice-mark': { borderColor: designTokens.color.action } },
    [selected]: {
      '& .HeydayChoice-mark': {
        color: contrastText,
        borderColor: designTokens.color.action,
        backgroundColor: designTokens.color.action,
        '&[data-kind="radio"]': { borderWidth: 5, backgroundColor: designTokens.color.canvas },
      },
    },
    '&.Mui-focusVisible .HeydayChoice-mark': {
      ...createFocusRingStyles(),
      boxShadow: `0 0 0 2px ${designTokens.color.canvas}`,
    },
    // native fieldset disabled에는 MUI-disabled class가 없어도 같은 표현을 적용합니다.
    '&.Mui-disabled .HeydayChoice-mark, &:has(input:disabled) .HeydayChoice-mark': { opacity: designTokens.button.disabledOpacity },
    [designTokens.media.touchTarget]: { width: 44, height: 44, padding: 14 },
    '@media (forced-colors: active)': {
      '& .HeydayChoice-mark': { forcedColorAdjust: 'none', borderColor: 'ButtonText', backgroundColor: 'Canvas' },
      [selected]: {
        '& .HeydayChoice-mark': { color: 'HighlightText', borderColor: 'Highlight', backgroundColor: 'Highlight', '&[data-kind="radio"]': { backgroundColor: 'Canvas' } },
      },
      '&.Mui-disabled .HeydayChoice-mark, &:has(input:disabled) .HeydayChoice-mark': { borderColor: 'GrayText' },
      '&.Mui-focusVisible .HeydayChoice-mark': { outlineColor: 'Highlight', boxShadow: '0 0 0 2px Canvas' },
    },
  };

  return {
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        icon: <ChoiceMark kind="checkbox" />,
        checkedIcon: <ChoiceMark kind="checkbox" state="checked" />,
        indeterminateIcon: <ChoiceMark kind="checkbox" state="mixed" />,
      },
      styleOverrides: { root: choiceRoot },
    },
    MuiRadio: {
      defaultProps: { disableRipple: true, icon: <ChoiceMark kind="radio" />, checkedIcon: <ChoiceMark kind="radio" state="checked" /> },
      styleOverrides: { root: choiceRoot },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
          gap: 4,
          alignItems: 'flex-start',
          // 클릭 영역과 표면은 원래 자리에 두어 옆 입력을 가리지 않습니다.
          // Stack의 margin 초기화와 무관하게 도형·글자만 입력의 시작선에 맞춥니다.
          '&.HeydayChoice-leadingAligned': {
            '& > .MuiCheckbox-root > .HeydayChoice-mark, & > .MuiRadio-root > .HeydayChoice-mark, & > .MuiFormControlLabel-label': {
              position: 'relative',
              insetInlineStart: -4,
              [designTokens.media.touchTarget]: { insetInlineStart: -14 },
            },
          },
          '&.MuiFormControlLabel-labelPlacementStart': { margin: 0 },
          '&.MuiFormControlLabel-labelPlacementTop, &.MuiFormControlLabel-labelPlacementBottom': { margin: 0, alignItems: 'center' },
          '&:has(> .MuiSwitch-root) .MuiFormControlLabel-label': { paddingBlock: 4, [designTokens.media.touchTarget]: { paddingBlock: 12 } },
          '&:has(input:disabled) .MuiFormControlLabel-label': { color: designTokens.color.primaryText, opacity: designTokens.button.disabledOpacity },
        },
        label: {
          ...designTokens.typography.ui,
          fontWeight: 400,
          minWidth: 0,
          overflowWrap: 'anywhere',
          paddingBlock: 2,
          '&.Mui-disabled': { color: designTokens.color.primaryText, opacity: designTokens.button.disabledOpacity },
          [designTokens.media.touchTarget]: { paddingBlock: 12 },
        },
      },
    },
    MuiFormGroup: { styleOverrides: { root: { '&[role="radiogroup"]': { gap: 8 } } } },
    MuiSwitch: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          width: 42,
          height: 28,
          padding: 4,
          overflow: 'visible',
          '&.MuiSwitch-sizeSmall': { width: 42, height: 28, padding: 4 },
          // switchBase/input은 이동하지 않습니다. 전체 클릭 영역을 유지하고 thumb만 이동합니다.
          '& .MuiSwitch-switchBase': {
            width: '100%', height: '100%', padding: 0, borderRadius: designTokens.shape.full,
            color: designTokens.color.canvas, transform: 'none',
            '&:hover': { backgroundColor: 'transparent' },
            '&.Mui-checked': {
              transform: 'none', color: contrastText,
              '&:hover': { backgroundColor: 'transparent' },
              '& .MuiSwitch-thumb': { transform: 'translate(14px, -50%)' },
              '& + .MuiSwitch-track': {
                opacity: 1, backgroundColor: designTokens.color.action, color: contrastText,
                '&::before': { left: 4, top: 6, width: 8, height: 5, borderLeft: '2px solid currentColor', borderBottom: '2px solid currentColor', backgroundColor: 'transparent', transform: 'rotate(-45deg)' },
                '&::after': { display: 'none' },
              },
            },
            '&.Mui-focusVisible + .MuiSwitch-track': { ...createFocusRingStyles(), boxShadow: `0 0 0 2px ${designTokens.color.canvas}` },
            '&.Mui-disabled': { color: designTokens.color.canvas },
            '&.Mui-checked.Mui-disabled': { color: contrastText },
            '& .MuiSwitch-input': { left: 0, width: '100%', height: '100%' },
          },
          '& .MuiSwitch-thumb': {
            position: 'absolute', left: 6, top: '50%', width: 16, height: 16,
            boxSizing: 'border-box', border: '1px solid transparent', boxShadow: 'none', transform: 'translateY(-50%)',
            transition: createMotionTransition('transform', 'feedback'),
          },
          '& .MuiSwitch-track': {
            position: 'relative', width: 34, height: 20, border: '1px solid transparent', boxSizing: 'border-box',
            borderRadius: designTokens.shape.full, backgroundColor: designTokens.color.secondaryText, color: designTokens.color.inverseText, opacity: 1,
            transition: createMotionTransition('background-color', 'feedback'),
            '&::before, &::after': { content: '""', position: 'absolute', left: 20, top: 8, width: 8, height: 1.5, backgroundColor: 'currentColor', transform: 'rotate(45deg)' },
            '&::after': { transform: 'rotate(-45deg)' },
          },
          '&:has(input:disabled)': { opacity: designTokens.button.disabledOpacity },
          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 1 },
          [designTokens.media.touchTarget]: {
            width: 44, height: 44, padding: '12px 5px',
            '&.MuiSwitch-sizeSmall': { width: 44, height: 44, padding: '12px 5px' },
            '& .MuiSwitch-thumb': { left: 7 },
          },
          '@media (forced-colors: active)': {
            '& .MuiSwitch-track': { forcedColorAdjust: 'none', borderColor: 'ButtonText', color: 'ButtonText', backgroundColor: 'Canvas' },
            '& .MuiSwitch-thumb': { forcedColorAdjust: 'none', color: 'Canvas', borderColor: 'ButtonText' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { color: 'HighlightText', backgroundColor: 'Highlight', borderColor: 'Highlight' },
            '& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track': { outlineColor: 'Highlight', boxShadow: '0 0 0 2px Canvas' },
          },
        },
      },
    },
  };
}
